import { InfoCircleOutlined } from '@ant-design/icons'
import {
  Button,
  Container,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material'
import { CompanySettings } from '@ulysses-inc/harami_api_client'
import React, { ReactElement, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Header } from 'src/components/header/Header'
import { HeaderTitle } from 'src/components/header/HeaderTitle'
import Loading from 'src/components/loading/Loading'
import LoadingOverlay from 'src/components/loading/LoadingOverlay'
import {
  getCompanySettings,
  updateCompanySettings,
} from 'src/state/ducks/companySettings/actions'
import { RootState } from 'src/state/store'

const CompanySettingsContainer: React.FC = () => {
  const dispatch = useDispatch()
  const [companySettings, setCompanySettings] =
    useState<CompanySettings | null>(null)

  const companySettingsState = useSelector(
    (state: RootState) =>
      state.companySettingsState.companySettings.companySettings,
  )

  const isLoading = useSelector(
    (state: RootState) => state.companySettingsState.companySettings.isLoading,
  )

  const isUpdating = useSelector(
    (state: RootState) =>
      state.companySettingsState.updateCompanySettings.isLoading,
  )

  useEffect(() => {
    dispatch(getCompanySettings())
  }, [dispatch])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- useEffect内でのsetState呼び出しは既存設計のため、リファクタリング時に修正する
    setCompanySettings(companySettingsState)
  }, [companySettingsState])

  const renderSwitch = (
    k: keyof CompanySettings,
    title: string | ReactElement,
  ) => (
    <Stack direction={'row'} alignItems={'center'}>
      <FormControlLabel
        control={
          <Switch
            checked={companySettings?.[k] === 1}
            onChange={event =>
              setCompanySettings(prev =>
                prev
                  ? {
                      ...prev,
                      [k]: event.target.checked ? 1 : 0,
                    }
                  : null,
              )
            }
          />
        }
        label={
          typeof title === 'string' ? (
            <Typography component={'span'} variant="body1">
              {title}
            </Typography>
          ) : (
            title
          )
        }
      />
    </Stack>
  )

  const onSave = () => {
    if (companySettings) {
      dispatch(updateCompanySettings(companySettings))
    }
  }

  return (
    <LoadingOverlay
      tip="アプリ設定を更新中です"
      spinning={isUpdating}
      size="large"
    >
      <Header>
        <HeaderTitle title="アプリ設定" />
      </Header>
      <Container maxWidth="sm">
        <Paper>
          <Stack spacing={2} mt={4} p={3}>
            {isLoading ? (
              <Loading />
            ) : (
              <>
                <Stack spacing={0.5}>
                  <Typography variant="subtitle1">
                    アプリ内の本日実施分画面に表示する項目を選択してください
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ※本設定は、記録アプリ（iOS/iPadOS）に対してのみ有効です
                  </Typography>
                </Stack>
                {renderSwitch('appShowHomeQrcode', 'QRから記入')}
                {renderSwitch('appShowHomeTemplate', 'ひな形から記録')}
                {renderSwitch(
                  'appShowHomeExpiredSchedule',
                  <Tooltip
                    arrow
                    placement="right"
                    title="オフにした場合、終了時刻を超過したスケジュールが表示されなくなります。"
                  >
                    <Typography variant="body1" component={'span'}>
                      終了時刻を超過したスケジュール <InfoCircleOutlined />
                    </Typography>
                  </Tooltip>,
                )}
                <Stack direction={'row'}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={onSave}
                    disabled={isLoading || !companySettings}
                  >
                    保存
                  </Button>
                </Stack>
              </>
            )}
          </Stack>
        </Paper>
      </Container>
    </LoadingOverlay>
  )
}

export default CompanySettingsContainer
